import { z } from 'zod';
import {
  CONTRACT_KINDS,
  PROJECT_STATUSES,
  RATE_UNITS,
  SERVICE_UNITS,
  STAFF_POSITION_STATUSES,
} from './enums';
import {
  expectedVersionSchema,
  idSchema,
  isoDateSchema,
  listParamsSchema,
  optionalDate,
  optionalText,
  requiredText,
  trimmed,
} from './schemas';

// ───────────── Модуль 1: реєстр посад, проєктів і донорів ─────────────

const optionalId = idSchema.nullish().transform((v) => v ?? null);
/** Сума в мінорних одиницях (копійки). */
const money = z.number().int().min(0).max(1e13);
const fte = z
  .number()
  .min(0.01, 'Мінімум 0,01')
  .max(10, 'Забагато')
  .transform((v) => Math.round(v * 100) / 100);

export const donorCreateSchema = z.object({ name: requiredText(200), notes: optionalText(2000) });
export const donorUpdateSchema = donorCreateSchema.partial().extend({ id: idSchema });

const projectFieldsBase = z.object({
  code: requiredText(50),
  name: requiredText(300),
  donorId: optionalId,
  startsAt: isoDateSchema,
  endsAt: optionalDate,
  budget: money,
  managerId: optionalId,
  status: z.enum(PROJECT_STATUSES),
  description: optionalText(5000),
});
const periodCheck = (v: { startsAt?: string | undefined; endsAt?: string | null | undefined }) =>
  !v.startsAt || !v.endsAt || v.endsAt >= v.startsAt;
const periodMessage = { message: 'Дата завершення раніше дати початку', path: ['endsAt'] };

export const projectFieldsSchema = projectFieldsBase
  .extend({
    budget: money.default(0),
    status: z.enum(PROJECT_STATUSES).default('active'),
  })
  .refine(periodCheck, periodMessage);
export type ProjectFieldsInput = z.input<typeof projectFieldsSchema>;

export const projectUpdateSchema = projectFieldsBase
  .partial()
  .extend({ id: idSchema })
  .extend(expectedVersionSchema.shape)
  .refine(periodCheck, periodMessage);

export const projectListSchema = listParamsSchema.extend({
  status: z.enum(PROJECT_STATUSES).optional(),
  donorId: idSchema.optional(),
});

export const budgetLineCreateSchema = z.object({
  projectId: idSchema,
  code: trimmed(50).default(''),
  name: requiredText(300),
  amount: money,
});
export const budgetLineUpdateSchema = z.object({
  id: idSchema,
  code: trimmed(50).optional(),
  name: requiredText(300).optional(),
  amount: money.optional(),
});

export const centerCreateSchema = z.object({
  name: requiredText(200),
  locationId: optionalId,
  managerId: optionalId,
  address: optionalText(500),
  /** Зона роботи: коди КАТОТТГ (області, райони, громади). */
  areaCodes: z.array(z.string().trim().max(20)).max(200).default([]),
});
export const centerUpdateSchema = centerCreateSchema.partial().extend({ id: idSchema });

export const serviceCreateSchema = z.object({
  code: trimmed(30).default(''),
  name: requiredText(300),
  description: optionalText(5000),
  unit: z.enum(SERVICE_UNITS).default('hour'),
});
export const serviceUpdateSchema = z.object({
  id: idSchema,
  code: trimmed(30).optional(),
  name: requiredText(300).optional(),
  description: optionalText(5000).optional(),
  unit: z.enum(SERVICE_UNITS).optional(),
  isArchived: z.boolean().optional(),
});

/** Критерій оцінки кандидата (шаблон на штатній одиниці; у конкурс копіюється). */
export const criterionSchema = z
  .object({
    name: requiredText(200),
    maxScore: z.number().int().min(1).max(100),
    weight: z.number().min(0.1).max(100),
    minScore: z
      .number()
      .int()
      .min(0)
      .max(100)
      .nullish()
      .transform((v) => v ?? null),
  })
  .refine((c) => c.minScore === null || c.minScore <= c.maxScore, {
    message: 'Мінімальний бал більший за максимальний',
    path: ['minScore'],
  });
export type CriterionInput = z.input<typeof criterionSchema>;

const staffPositionFieldsBase = z.object({
  projectId: idSchema,
  positionId: idSchema,
  centerId: optionalId,
  locationId: optionalId,
  budgetLineId: optionalId,
  contractKind: z.enum(CONTRACT_KINDS),
  fte,
  rate: money,
  rateUnit: z.enum(RATE_UNITS),
  startsAt: isoDateSchema,
  endsAt: optionalDate,
  status: z.enum(STAFF_POSITION_STATUSES),
  /** Опис посади і функціональні обов'язки (HTML редактора). */
  duties: z.string().max(100_000),
  /** Вимоги до кандидатів: освіта, досвід, інше. */
  requirements: optionalText(10_000),
  criteria: z.array(criterionSchema).max(30),
  serviceIds: z.array(idSchema).max(200),
});

export const staffPositionFieldsSchema = staffPositionFieldsBase
  .extend({
    contractKind: z.enum(CONTRACT_KINDS).default('employment'),
    fte: fte.default(1),
    rate: money.default(0),
    rateUnit: z.enum(RATE_UNITS).default('month'),
    status: z.enum(STAFF_POSITION_STATUSES).default('active'),
    duties: z.string().max(100_000).default(''),
    criteria: z.array(criterionSchema).max(30).default([]),
    serviceIds: z.array(idSchema).max(200).default([]),
  })
  .refine(periodCheck, periodMessage);
export type StaffPositionFieldsInput = z.input<typeof staffPositionFieldsSchema>;

export const staffPositionUpdateSchema = staffPositionFieldsBase
  .partial()
  .extend({ id: idSchema })
  .extend(expectedVersionSchema.shape)
  .refine(periodCheck, periodMessage);
export type StaffPositionUpdateInput = z.input<typeof staffPositionUpdateSchema>;

export const staffPositionListSchema = listParamsSchema.extend({
  projectId: idSchema.optional(),
  centerId: idSchema.optional(),
  locationId: idSchema.optional(),
  status: z.enum(STAFF_POSITION_STATUSES).optional(),
});

/** Типовий набір критеріїв оцінки (якщо для посади не задано свій). */
export const DEFAULT_CRITERIA: CriterionInput[] = [
  { name: 'Освіта', maxScore: 10, weight: 1, minScore: null },
  { name: 'Досвід роботи за напрямом', maxScore: 10, weight: 2, minScore: 3 },
  { name: 'Співбесіда: професійні знання', maxScore: 10, weight: 2, minScore: null },
  { name: 'Мотивація і цінності організації', maxScore: 10, weight: 1, minScore: null },
];
